import type { AttemptResult } from "./results";
import { RESULT_POINTS_DECIMALS, RESULT_POINTS_LIMIT, RESULT_VALUE_DECIMALS, RESULT_VALUE_LIMIT } from "./results";
import type { ResultType } from "./vocab";

/**
 * Marks typed by a person → the canonical unit of a `result_type`, and back.
 * Moved here from OVR's `lib/entry/{time,distance,decimal}.ts` (2026-10-29)
 * so the GMS (qualifying standards, a shortlisted athlete's best record),
 * the API and OVR parse one way. OVR re-exports these.
 *
 *   TIME      integer milliseconds   "52.31", "1:52.31", "1.02.03.40"
 *   DISTANCE  integer millimetres    "7.85", "7,85 m"
 *   WEIGHT    kilograms              "142.5"
 *   POINTS / SCORE  as counted       "8123", "13.533"
 *
 * A value is either parsed exactly or refused with a reason — never
 * truncated, never guessed, never through a float (7.85 * 1000 is 7849.999…).
 */

// ─── Times ───────────────────────────────────────────────────────────────────

/*
 * Accepted (surrounding spaces ignored):
 *
 *   52.31   52,31   52.3   52      seconds (comma or point as the decimal mark)
 *   1:52.31  1:52,31  1:52          minutes : seconds
 *   1:02:03.4                        hours : minutes : seconds
 *   1.52.31  1,52,31  1.02.03.40     the same, typed with points or commas
 *
 * The rule for points / commas: a colon always separates hours / minutes /
 * seconds. A point or comma is the DECIMAL mark when it is the last separator,
 * and an hours / minutes separator otherwise. So "1.52" is 1.52 seconds and
 * "1.52.31" is 1:52.31 — decided because a numeric keypad has no colon key,
 * and with two or more separators the reading is unambiguous.
 *
 * Every group after the first must be exactly two digits and below 60
 * ("1:5.31" is refused, not read as 1:05.31 or 1:50.31). Seconds alone may be
 * 60 or more ("75.3" = 1:15.30). The whole time must be under 24 hours.
 */

export type TimeParseFailure = "empty" | "format" | "precision" | "range";
export type TimeParseResult = { ok: true; ms: number } | { ok: false; reason: TimeParseFailure };

/** Decimals of a second: tenths, hundredths (the default) or thousandths. */
export type TimePrecision = 1 | 2 | 3;

/** The longest time accepted: 24 hours, exclusive. Covers every timed event of a Games. */
export const TIME_MAX_MS = 24 * 60 * 60 * 1000;

const TIME_SHAPE = /^(\d+)((?:[:.,]\d+)*)$/;

/**
 * Parses a typed time. `precision` is the most decimals accepted (default 2,
 * hundredths); more decimals are refused as `precision`, never rounded.
 *
 *   empty      nothing typed
 *   format     not a time ("1:5.31", "52a", "1:2:3:4", "-5")
 *   precision  more decimals than the event times to
 *   range      a minutes / seconds group of 60 or more, or 24 hours or more
 */
export function parseTime(input: string, options: { precision?: TimePrecision } = {}): TimeParseResult {
	const precision = options.precision ?? 2;
	const text = input.trim();
	if (text === "") return { ok: false, reason: "empty" };
	const shape = TIME_SHAPE.exec(text);
	if (shape === null) return { ok: false, reason: "format" };

	const groups = text.split(/[:.,]/);
	const separators = text.match(/[:.,]/g) ?? [];
	const last = separators[separators.length - 1];
	const hasFraction = last === "." || last === ",";
	const fraction = hasFraction ? (groups.pop() as string) : "";
	if (groups.length > 3) return { ok: false, reason: "format" };
	if (groups.slice(1).some((g) => g.length !== 2)) return { ok: false, reason: "format" };
	if (fraction.length > precision) return { ok: false, reason: "precision" };

	const numbers = groups.map(Number);
	if (numbers.slice(1).some((n) => n >= 60)) return { ok: false, reason: "range" };
	const [seconds = 0, minutes = 0, hours = 0] = numbers.reverse();
	const ms = ((hours * 60 + minutes) * 60 + seconds) * 1000 + Number(fraction.padEnd(3, "0"));
	if (!Number.isSafeInteger(ms) || ms >= TIME_MAX_MS) return { ok: false, reason: "range" };
	return { ok: true, ms };
}

/** How many decimals `ms` needs so that no non-zero digit is hidden. */
function decimalsNeeded(ms: number): number {
	if (ms % 10 !== 0) return 3;
	if (ms % 100 !== 0) return 2;
	if (ms % 1000 !== 0) return 1;
	return 0;
}

/**
 * Formats milliseconds the way a scoreboard prints them, and the way
 * `result_display` stores them: `52.31`, `1:52.31`, `1:02:03.40`. Minutes and
 * hours are not padded; inner groups are two digits. Always a point and ASCII
 * digits, whatever the viewer's language — this is the sport's notation, not
 * a localized number.
 *
 * `precision` is the MINIMUM number of decimals (default 2). A digit is never
 * hidden: 52314 ms at precision 2 prints `52.314`, so the display cannot
 * disagree with the stored value. Sub-millisecond input is rounded to the
 * millisecond; a negative or non-finite value prints "".
 */
export function formatTime(ms: number, options: { precision?: TimePrecision } = {}): string {
	if (!Number.isFinite(ms) || ms < 0) return "";
	const whole = Math.round(ms);
	const decimals = Math.max(options.precision ?? 2, decimalsNeeded(whole));
	const totalSeconds = Math.floor(whole / 1000);
	const fraction = String(whole % 1000)
		.padStart(3, "0")
		.slice(0, decimals);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	const pad = (n: number) => String(n).padStart(2, "0");
	const clock = hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : minutes > 0 ? `${minutes}:${pad(seconds)}` : String(seconds);
	return decimals > 0 ? `${clock}.${fraction}` : clock;
}

// ─── Distances ───────────────────────────────────────────────────────────────

/*
 * Field-event marks typed in metres → integer millimetres (the canonical unit
 * of a DISTANCE `result_value` and of every ATTEMPTS mark, `results.ts`).
 * Accepted: `7.85`, `7,85`, `7.8`, `7`, `.85`, an optional trailing `m`
 * (`7.85 m`), surrounding spaces. A comma is always the decimal mark — there
 * is no thousands separator in a mark.
 */

export type DistanceParseFailure = "empty" | "format" | "precision" | "range";
export type DistanceParseResult = { ok: true; mm: number } | { ok: false; reason: DistanceParseFailure };

/** Decimals of a metre: centimetres (the default: World Athletics records field marks to the
 * centimetre) or millimetres. */
export type DistancePrecision = 2 | 3;

/** The longest mark accepted: 1000 m, exclusive. A javelin world record is under 100 m. */
export const DISTANCE_MAX_MM = 1_000_000;

const DISTANCE_SHAPE = /^(\d*)(?:[.,](\d*))?\s*m?$/i;

/**
 * Parses a mark in metres. `precision` is the most decimals accepted (default
 * 2); more are refused as `precision`, never rounded.
 */
export function parseDistance(input: string, options: { precision?: DistancePrecision } = {}): DistanceParseResult {
	const precision = options.precision ?? 2;
	const text = input.trim();
	if (text === "") return { ok: false, reason: "empty" };
	const shape = DISTANCE_SHAPE.exec(text);
	if (shape === null) return { ok: false, reason: "format" };
	const metres = shape[1] ?? "";
	const fraction = shape[2] ?? "";
	if (metres === "" && fraction === "") return { ok: false, reason: "format" };
	if (fraction.length > precision) return { ok: false, reason: "precision" };
	const mm = Number(metres || "0") * 1000 + Number(fraction.padEnd(3, "0"));
	if (!Number.isSafeInteger(mm) || mm >= DISTANCE_MAX_MM) return { ok: false, reason: "range" };
	return { ok: true, mm };
}

/**
 * Formats millimetres as metres for the board and `result_display`: `7.85`.
 * `precision` is the MINIMUM decimals (default 2); a non-zero millimetre is
 * never hidden (7853 → `7.853`). Always a point and ASCII digits. A negative or
 * non-finite value prints "".
 */
export function formatDistance(mm: number, options: { precision?: DistancePrecision } = {}): string {
	if (!Number.isFinite(mm) || mm < 0) return "";
	const whole = Math.round(mm);
	const decimals = whole % 10 !== 0 ? 3 : (options.precision ?? 2);
	return `${Math.floor(whole / 1000)}.${String(whole % 1000)
		.padStart(3, "0")
		.slice(0, decimals)}`;
}

/** One cell of an attempts grid, as typed. */
export type MarkEntry = { result: Extract<AttemptResult, "VALID">; mark: number } | { result: Extract<AttemptResult, "FOUL" | "PASS"> };
export type MarkEntryResult = { ok: true; entry: MarkEntry } | { ok: false; reason: DistanceParseFailure };

/**
 * An attempt cell: a mark, or a one-key shortcut — `x` / `X` for a foul,
 * `-` (or an en / em dash) for a pass. The shortcuts are how a field judge's
 * card is written (study B: "X / − are shortcuts for foul / pass").
 */
export function parseMarkEntry(input: string, options: { precision?: DistancePrecision } = {}): MarkEntryResult {
	const text = input.trim();
	if (text === "x" || text === "X") return { ok: true, entry: { result: "FOUL" } };
	if (text === "-" || text === "–" || text === "—") return { ok: true, entry: { result: "PASS" } };
	const parsed = parseDistance(text, options);
	return parsed.ok ? { ok: true, entry: { result: "VALID", mark: parsed.mm } } : parsed;
}

// ─── Decimals (scores, points, weights) ──────────────────────────────────────

/*
 * Scores and points typed by an operator → a number, with a comma or a point
 * as the decimal mark. A comma is ALWAYS the decimal mark, never a thousands
 * separator: "8,123" is 8.123. Where that reading would be dangerous
 * (decathlon points, a set score), use `parseInteger`, which refuses any
 * separator. The limits come from `RESULT_*` (the database's numeric precision).
 */

export type DecimalParseFailure = "empty" | "format" | "precision" | "range";
export type DecimalParseResult = { ok: true; value: number } | { ok: false; reason: DecimalParseFailure };

export interface DecimalOptions {
	/** Most decimals accepted; trailing zeros do not count ("13.500" has 1). */
	maxDecimals: number;
	/** Inclusive bounds. A minus sign is accepted only when `min` is below 0. */
	min?: number | undefined;
	max?: number | undefined;
	/** `|value|` must be BELOW this (the contracts' `RESULT_*_LIMIT`). */
	limit?: number | undefined;
}

const DECIMAL_SHAPE = /^([+-]?)(\d*)(?:[.,](\d*))?$/;

/**
 * Parses a decimal: `13.533`, `13,533`, `-0.4`, `.5`, `7.` (a trailing mark is
 * ignored). Surrounding spaces are ignored; anything else is `format`.
 *
 *   precision  more decimals than `maxDecimals` (refused, never rounded)
 *   range      outside `min` / `max` / `limit`, or negative where `min` ≥ 0
 */
export function parseDecimal(input: string, options: DecimalOptions): DecimalParseResult {
	const text = input.trim();
	if (text === "") return { ok: false, reason: "empty" };
	const shape = DECIMAL_SHAPE.exec(text);
	if (shape === null) return { ok: false, reason: "format" };
	const sign = shape[1] ?? "";
	const whole = shape[2] ?? "";
	const fraction = (shape[3] ?? "").replace(/0+$/, "");
	if (whole === "" && (shape[3] ?? "") === "") return { ok: false, reason: "format" };
	if (fraction.length > options.maxDecimals) return { ok: false, reason: "precision" };
	const value = Number(`${sign}${whole || "0"}.${fraction || "0"}`);
	if (!Number.isFinite(value)) return { ok: false, reason: "range" };
	if (sign === "-" && (options.min === undefined || options.min >= 0) && value !== 0) return { ok: false, reason: "range" };
	if (options.min !== undefined && value < options.min) return { ok: false, reason: "range" };
	if (options.max !== undefined && value > options.max) return { ok: false, reason: "range" };
	if (options.limit !== undefined && Math.abs(value) >= options.limit) return { ok: false, reason: "range" };
	// -0 is 0.
	return { ok: true, value: value === 0 ? 0 : value };
}

/**
 * Parses a whole number (a set score, goals, decathlon points). Any decimal
 * mark is refused as `format` — "8,123" is never quietly read as 8.123 or 8123.
 */
export function parseInteger(input: string, options: { min?: number; max?: number } = {}): DecimalParseResult {
	const text = input.trim();
	if (/[.,]/.test(text)) return { ok: false, reason: "format" };
	return parseDecimal(text, { maxDecimals: 0, min: options.min ?? 0, max: options.max });
}

/** A `result_value` that is not a time or a distance (POINTS, SCORE, WEIGHT): ≤ 4 decimals, `|v| < 10^10`. */
export function parseResultValue(input: string, options: { min?: number } = {}): DecimalParseResult {
	return parseDecimal(input, { maxDecimals: RESULT_VALUE_DECIMALS, limit: RESULT_VALUE_LIMIT, min: options.min ?? 0 });
}

/** `points` (match points, combined-event points): ≤ 3 decimals, `|p| < 10^7`. Negative allowed (a points deduction). */
export function parsePoints(input: string): DecimalParseResult {
	return parseDecimal(input, { maxDecimals: RESULT_POINTS_DECIMALS, limit: RESULT_POINTS_LIMIT, min: -RESULT_POINTS_LIMIT });
}

/**
 * Formats a score for the board and `result_display`: at least `minDecimals`
 * (default 0), at most `maxDecimals` (default `RESULT_VALUE_DECIMALS`), trailing
 * zeros beyond the minimum dropped — 13.6 with `minDecimals: 3` → `13.600`.
 * Always a point and ASCII digits. A non-finite value prints "".
 */
export function formatDecimal(value: number, options: { minDecimals?: number; maxDecimals?: number } = {}): string {
	if (!Number.isFinite(value)) return "";
	const min = options.minDecimals ?? 0;
	const max = Math.max(min, options.maxDecimals ?? RESULT_VALUE_DECIMALS);
	const rounded = value.toFixed(max);
	// A value that rounds to zero prints "0", never "-0".
	const fixed = Number(rounded) === 0 ? (0).toFixed(max) : rounded;
	if (max === min) return fixed;
	const [whole, fraction = ""] = fixed.split(".");
	const kept = fraction.replace(/0+$/, "").padEnd(min, "0");
	return kept === "" ? (whole as string) : `${whole}.${kept}`;
}

// ─── Marks: one value of a result type (qualifying standards, best records) ──

/**
 * A stored mark (`events.qualifying_mark`, `shortlist_entries.best_mark`) is
 * `numeric(14,3)` in the canonical unit of the event's `result_type`: it holds
 * values ≥ 0, below MARK_LIMIT, with at most MARK_DECIMALS decimals. TIME and
 * DISTANCE are whole numbers (ms, mm) by construction.
 */
export const MARK_LIMIT = 1e11;
export const MARK_DECIMALS = 3;

/** `no_measure`: the event's result type is NONE — it has no mark to type. */
export type MarkParseFailure = "empty" | "format" | "precision" | "range" | "no_measure";
export type MarkParseResult = { ok: true; value: number } | { ok: false; reason: MarkParseFailure };

/**
 * Parses a mark typed for an event of `resultType` into its canonical unit:
 * TIME → ms (thousandths accepted), DISTANCE → mm (millimetres accepted),
 * WEIGHT / POINTS / SCORE → the number (≤ MARK_DECIMALS decimals, ≥ 0).
 * NONE → `no_measure`.
 */
export function parseMark(resultType: ResultType, text: string): MarkParseResult {
	switch (resultType) {
		case "TIME": {
			const r = parseTime(text, { precision: 3 });
			return r.ok ? { ok: true, value: r.ms } : r;
		}
		case "DISTANCE": {
			const r = parseDistance(text, { precision: 3 });
			return r.ok ? { ok: true, value: r.mm } : r;
		}
		case "WEIGHT":
		case "POINTS":
		case "SCORE":
			return parseDecimal(text, { maxDecimals: MARK_DECIMALS, min: 0, limit: MARK_LIMIT });
		case "NONE":
			return text.trim() === "" ? { ok: false, reason: "empty" } : { ok: false, reason: "no_measure" };
	}
}

/**
 * A canonical value as the sport writes it: TIME `1:52.31`, DISTANCE `7.85`,
 * the rest `142.5`. `parseMark(t, formatMark(t, v))` gives `v` back for every
 * value `parseMark` can produce. A non-finite or negative value prints "".
 */
export function formatMark(resultType: ResultType, value: number): string {
	if (!Number.isFinite(value) || value < 0) return "";
	switch (resultType) {
		case "TIME":
			return formatTime(value, { precision: 2 });
		case "DISTANCE":
			return formatDistance(value);
		default:
			return formatDecimal(value, { maxDecimals: MARK_DECIMALS });
	}
}

/**
 * Does `mark` meet `standard`? `lowerIsBetter` is the event's direction
 * (times: lower; distances / points: higher); equal meets it.
 *   standard null → null (the event has no standard: nothing to meet);
 *   mark null     → false (a standard, and no record given).
 * The API's `meetsStandardSql` (shortlist reads) is the same rule.
 */
export function meetsStandard(mark: number | null, standard: number | null, lowerIsBetter: boolean): boolean | null {
	if (standard === null) return null;
	if (mark === null) return false;
	return lowerIsBetter ? mark <= standard : mark >= standard;
}
